import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, X, Sparkles, Clock, Calendar, Video, FileText, Gift,
  Crown, Package, Rocket, UserCheck, Award, Shield, Globe, BarChart3, Phone,
  MessageCircle, Target, Flame, ChevronLeft, TrendingUp, Users, Zap, Headphones,
} from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import CheckoutModal from '../components/landing/CheckoutModal';
import StudentResultsSection from '../components/landing/StudentResultsSection';
import { usePromo } from '../context/PromoContext';

const COURSE_SCREENSHOT = "https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/cvbekj5n_image.png";
const FLOW_CALL_1 = "https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/ix5yahu1_image.png";
const FLOW_CALL_2 = "https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/6ty8b2fn_image.png";
const FLOW_CALL_3 = "https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/fsju11zb_image.png";
const GROUP_CHAT  = "https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/whu6dtgf_image.png";

// ============== CONFIG ==============
const tiers = {
  myself: {
    packageId: 'base',
    name: 'Обучаюсь сам',
    accent: 'amber',
    icon: Package,
    tagline: 'Самостоятельное обучение',
    hero_desc: 'Полный курс Amazon Wholesale без личных созвонов: 75 видеоуроков, авторский софт, записи 9 групповых сессий и поддержка в чате. Для тех, кто привык разбираться сам и не хочет переплачивать за менторство.',
    chips: ['75 видео-уроков', 'Доступ навсегда', 'ASIN Checker в подарок', 'Записи 9 созвонов'],
    fits: [
      'Уже есть базовый опыт e-commerce — нужна именно методология Amazon',
      'Готов учиться самостоятельно, по плану, без напоминаний',
      'Хочешь сначала разобраться в системе, потом, может, перейти на тариф с менторством',
      'Есть время — 1-2 часа в день на учёбу и домашки',
    ],
    not_fits: [
      'Нужен «волшебный пинок» и контроль каждого шага',
      'Хочешь стартовать прямо завтра — без понимания основ',
      'Не готов задавать вопросы в чат и читать домашки',
    ],
    features: [
      { icon: Video,    title: '75 видео-уроков на SkillSpace', desc: 'Полная программа: от регистрации компании до выхода на первые $1 000. Без воды. С практическими домашками.' },
      { icon: Sparkles, title: 'Авторский софт ASIN Checker',   desc: 'Проверка открытых и закрытых товаров на Amazon. Обычная цена $350 — для вас бесплатно, навсегда.' },
      { icon: Headphones, title: 'Записи 9 групповых созвонов', desc: 'Полные записи разборов с прошлых потоков: юр.оформление, регистрация, поставщики, выбор товаров, аналитика, масштабирование.' },
      { icon: MessageCircle, title: 'Поддержка в чате — 1 месяц', desc: 'Любой вопрос по Amazon или обучению — пишете Вадиму и получаете ответ. Без личных созвонов.' },
      { icon: Gift,     title: 'Бонусы в подарок — $1 750',     desc: 'Ангейтинг, Поставщик-инвойс, Sourcing Pack, ASIN Checker, Репрайсер. Открываются сразу после оплаты.' },
      { icon: Shield,   title: 'Доступ навсегда',                desc: 'Никаких сроков на материалах. Возвращаетесь к урокам через год — всё на месте. Обновления входят.' },
    ],
    show_course_screenshot: true,
    show_group_calls: false,
    show_personal_calls: false,
    show_roadmap: false,
    show_vip_block: false,
    final_message: 'Вы покупаете не «доступ», а готовый план запуска Amazon-бизнеса. Дальше — только работа.',
  },

  potok: {
    packageId: 'flow',
    name: 'Поток',
    accent: 'blue',
    icon: Rocket,
    tagline: 'Групповое обучение',
    hero_desc: 'Один месяц интенсива в небольшой группе. За 9 еженедельных созвонов вы доходите до первой партии товара на складе Amazon. Поток с конкретной датой старта — это дисциплина, которой не хватает в самостоятельном обучении.',
    chips: ['9 групповых созвонов', '2 личных созвона', '1 проверенный поставщик', 'Поддержка 24/7'],
    fits: [
      'Хочешь учиться в группе с одинаковой целью — это сильно ускоряет',
      'Готов держать ритм 1 неделя = 1 крупный шаг',
      'Ценишь конкретную дату старта, а не «когда-нибудь»',
      'Бюджет на первую партию — от $4 000',
    ],
    not_fits: [
      'Нет 2-3 часов в неделю на созвон + домашку',
      'Хочешь, чтобы за тебя сделали всю работу — это не VIP-тариф',
    ],
    features: [
      { icon: Rocket,     title: '9 групповых созвонов за месяц',  desc: 'По одному созвону в неделю. Интерактивно: каждый задаёт вопросы, разбираем реальные кейсы группы. Все созвоны записываются — пропустил, посмотрел запись.' },
      { icon: UserCheck,  title: '2 личных созвона с Вадимом',     desc: 'Только вы и я. Разбираем именно вашу ситуацию — кабинет, документы, выбор товара. Записи остаются у вас.' },
      { icon: Package,    title: '1 проверенный поставщик',         desc: 'Подключаем поставщика, который одобрит ваш аккаунт под закупку от $4 000 с ROI от 20%. Не «база контактов» — конкретный одобренный поставщик под вас.' },
      { icon: Video,      title: '75 видео-уроков на SkillSpace',   desc: 'Полная теория параллельно с созвонами. Изучаете в своём темпе между сессиями.' },
      { icon: Sparkles,   title: 'ASIN Checker — бесплатно',        desc: 'Софт для проверки товаров на Amazon. $350 в подарок, доступ навсегда.' },
      { icon: MessageCircle, title: '3 месяца поддержки в чате',    desc: 'После окончания потока ещё 3 месяца — пишете вопросы, получаете ответы. Доводим до первых продаж.' },
      { icon: Gift,       title: 'Бонусы $1 750 в подарок',          desc: 'Ангейтинг, Поставщик-инвойс, Sourcing Pack, Репрайсер. Открываются сразу.' },
    ],
    show_course_screenshot: true,
    show_group_calls: true,
    show_personal_calls: false,
    show_roadmap: 'flow',
    show_vip_block: false,
    final_message: '4 недели → первая партия → первые продажи → понимание цифр. Дальше работаете сами — или переходите на тариф с большим менторством.',
  },

  advanced: {
    packageId: 'advanced',
    name: 'Продвинутый',
    accent: 'violet',
    icon: UserCheck,
    tagline: 'С личным менторством',
    hero_desc: 'Всё из «Обучаюсь сам» плюс плотное личное менторство: 4 разбора 1-на-1 по 1.5 часа, 3 одобренных поставщика для старта и 4 месяца поддержки. Это формат для тех, кто понимает, что цена ошибки в Amazon — несколько тысяч долларов, и не хочет учиться на своих.',
    chips: ['4 личных созвона', '3 поставщика для старта', 'Выбор рынка ЕС/США', '4 месяца поддержки'],
    fits: [
      'Уже есть стартовый капитал $5 000+ и желание ускориться',
      'Понимаешь, что лично разобрать ситуацию у наставника — быстрее, чем 10 раз перечитывать материалы',
      'Готов работать сам, но хочешь, чтобы критические шаги проверил человек с 6-летним опытом',
      'Хочешь выбрать между Amazon US или EU — нужен взгляд со стороны',
    ],
    not_fits: [
      'Бюджета только на сам курс — без денег на первую закупку',
      'Хочешь, чтобы команда вела бизнес за тебя — для этого есть «Про уровень»',
    ],
    features: [
      { icon: UserCheck,  title: '4 личных созвона по 1.5 часа',   desc: 'Глубокие разборы 1-на-1. На первом созвоне строим план под вашу ситуацию. На остальных — корректируем по ходу.' },
      { icon: Package,    title: '3 проверенных поставщика',        desc: 'Не контакты «из интернета». Это 3 оптовика, которые одобрят ваш аккаунт под закупки от $4 000 с маржой 20%+. Один из них — точно под ваш рынок.' },
      { icon: Globe,      title: 'Выбор рынка: Amazon US или EU',   desc: 'Помогаю определиться по вашей географии, юр.лицу, бюджету и языку. На каждом рынке свои нюансы — не нужно повторять чужие ошибки.' },
      { icon: Video,      title: '75 видео-уроков + 9 записей',     desc: 'Полная теория + записи всех групповых созвонов прошлых потоков.' },
      { icon: Sparkles,   title: 'ASIN Checker — бесплатно',        desc: '$350 в подарок, доступ навсегда.' },
      { icon: MessageCircle, title: '4 месяца поддержки в чате',    desc: 'Пишете в любой момент — отвечаю в течение дня. По сути это 1 на 1 поддержка после курса.' },
      { icon: Gift,       title: 'Бонусы $1 750 + ускоренный старт', desc: 'Все продукты-бонусы плюс готовая последовательность шагов для немедленного запуска.' },
    ],
    show_course_screenshot: true,
    show_group_calls: false,
    show_personal_calls: true,
    show_roadmap: false,
    show_vip_block: false,
    final_message: 'Это тариф для тех, кто не хочет «учиться» — а хочет выстроить систему и начать зарабатывать. С персональным разбором каждого шага.',
  },

  vip: {
    packageId: 'pro',
    name: 'Про уровень',
    accent: 'emerald',
    icon: Crown,
    tagline: 'Максимальный результат — бизнес под ключ',
    hero_desc: 'Не «курс с менторством». Это сопровождение под ключ к обороту $15 000+/мес. Мы собираем для вас инфраструктуру (сайт + CRM + воронка), подключаем 10 поставщиков, помогаем выкупить и отправить первые 50 SKU, и 6 месяцев ведём за руку до устойчивых продаж. Формат для тех, кто пришёл за бизнесом, а не за знаниями.',
    chips: ['12 личных созвонов', '10 поставщиков + 50 SKU', 'Кастомный сайт + CRM', '6 месяцев сопровождения'],
    fits: [
      'Цель — быстро выйти на $15 000+ оборота/мес, не «потестировать модель»',
      'Бюджет $15 000+ на закупки в первые 3 месяца',
      'Готов делегировать рутину команде, а не делать всё сам',
      'Хочешь, чтобы инфраструктура (сайт, CRM, аналитика) была собрана за тебя',
    ],
    not_fits: [
      'Бюджета меньше $5 000 на закупки — экономика не сойдётся',
      'Хочешь сначала «попробовать» — для пробы есть «Поток»',
    ],
    features: [
      { icon: Phone,     title: '12 личных созвонов',              desc: 'Раз в 2 недели в течение полугода. Разбираем каждый шаг: документы, поставщики, листинги, аналитика, репрайсер, масштабирование.' },
      { icon: Package,   title: '10 проверенных поставщиков',      desc: 'Не «база» — 10 оптовиков, под которые мы вместе оформим аккаунты и согласуем условия. На разных категориях, чтобы диверсифицировать риски.' },
      { icon: Target,    title: '50 SKU для анализа и закупки',     desc: 'Совместно отбираем 50 товаров с маржой и спросом. Из них формируем первые партии — это уже не «искать товар», это «выбирать из готового списка».' },
      { icon: Globe,     title: 'Кастомный сайт под ваш бренд',     desc: 'Персональный сайт с сегментацией аудитории, оплатой и премиум-дизайном. Для оптовиков — обычная цена $1 500+, для вас входит.' },
      { icon: BarChart3, title: 'CRM-система под вас',              desc: 'Кастомная CRM: контроль сотрудников, учёт брендов, задачи, чат, уведомления. База, в которой видна вся воронка с каждым поставщиком. Обычная цена $1 500+ — входит.' },
      { icon: TrendingUp, title: 'Воронка продаж + Sellerboard',    desc: 'Настраиваем учёт прибыли, репрайсер, контроль ключевых цифр. Вы видите бизнес в одной таблице.' },
      { icon: MessageCircle, title: '6 месяцев личной поддержки',  desc: 'Я отвечаю на ваши сообщения как личный консультант — без очереди и группового чата. Полгода — до устойчивого выхода в плюс.' },
      { icon: Gift,      title: 'Все бонусы $1 750 + VIP-эксклюзив', desc: 'Кроме общих бонусов — VIP-пакет ($3 000+ в подарок): кастомный сайт и CRM.' },
    ],
    show_course_screenshot: true,
    show_group_calls: false,
    show_personal_calls: true,
    show_roadmap: 'pro',
    show_vip_block: true,
    final_message: 'Цель этого тарифа — не «дать вам курс». Цель — чтобы через 6 месяцев у вас был работающий Amazon-бизнес с оборотом от $15 000/мес, который вы понимаете и контролируете.',
  },
};

// Flow roadmap (compact)
const flowRoadmap = [
  { week: 'Неделя 0', title: 'После оплаты',     desc: 'Доступ к SkillSpace (75 уроков), ASIN Checker, все бонусы. Инструкция как готовиться к старту потока.', icon: Gift },
  { week: 'Недели 1-2', title: 'Запуск',         desc: 'Юр.оформление, регистрация Amazon-аккаунта. Созвоны 1-2. Проверяем ваши шаги в чате.', icon: Rocket },
  { week: 'Недели 3-5', title: 'Поставщики + товары', desc: 'Созвоны 3-5. Поиск поставщиков, выбор товаров, глубокий анализ. Собираете шорт-лист через ASIN Checker.', icon: Package },
  { week: 'Недели 6-7', title: 'Отправка', desc: 'Созвоны 6-7. Prep-центры, упаковка, маркировка, shipment на склад Amazon.', icon: Award },
  { week: 'Недели 8-9', title: 'Первые продажи', desc: 'Созвоны 8-9. Sellerboard, репрайсер, контроль прибыли, начало масштабирования.', icon: BarChart3 },
  { week: 'Месяц 2-4', title: 'Поддержка',     desc: 'Ещё 3 месяца чат-поддержки. Повторяете цикл закупок самостоятельно с разбором в чате.', icon: TrendingUp },
];

// Pro roadmap
const proRoadmap = [
  { week: 'Этап 1',  title: 'Старт и стратегия',          desc: 'Личный созвон, оценка ситуации. Документы, компания, аккаунты. Индивидуальный план.',  icon: Rocket },
  { week: 'Этап 2',  title: 'Инфраструктура',             desc: 'Запускаем разработку сайта и CRM. Настраиваем Google-таблицы, Sellerboard, фин.учёт.', icon: BarChart3 },
  { week: 'Этап 3',  title: 'Поставщики и товары',        desc: '10 проверенных поставщиков, совместные регистрации, анализ каталогов, 50 SKU.',         icon: Package },
  { week: 'Этап 4',  title: 'Amazon и продажи',           desc: 'Ангейтинг, отправка на склады, репрайсер, первые активные листинги и движение денег.', icon: Target },
  { week: 'Этап 5',  title: 'Самостоятельность',          desc: 'Самостоятельный поиск нового поставщика, вторая закупка — по вашей инициативе.',        icon: TrendingUp },
];

// Accent palette
const accents = {
  amber:   { text: 'text-amber-400',   ring: 'border-amber-500/40',   from: 'from-amber-500',  to: 'to-orange-500', glow: 'shadow-amber-500/30',   chip: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  blue:    { text: 'text-blue-400',    ring: 'border-blue-500/40',    from: 'from-blue-500',   to: 'to-indigo-500', glow: 'shadow-blue-500/30',    chip: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  violet:  { text: 'text-violet-400',  ring: 'border-violet-500/40',  from: 'from-violet-500', to: 'to-purple-500', glow: 'shadow-violet-500/30',  chip: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  emerald: { text: 'text-emerald-400', ring: 'border-emerald-500/40', from: 'from-emerald-500',to: 'to-teal-500',   glow: 'shadow-emerald-500/30', chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
};

const TierDetailPage = ({ tierKey, discountMode = false }) => {
  const tier = tiers[tierKey];
  const a = accents[tier.accent];
  const HeroIcon = tier.icon;
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [serverPrice, setServerPrice] = useState(0);
  const [settings, setSettings] = useState(null);
  const { shouldApplyDiscount, remainingSeconds, hasValidPromo, isExpired } = usePromo();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${tier.name} — Amazon Wholesale | Vadjik`;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/packages`)
      .then((r) => r.json())
      .then((data) => {
        const p = data?.[tier.packageId]?.price;
        if (p) setServerPrice(p);
      })
      .catch(() => {});
    if (discountMode) {
      fetch(`${API}/api/admin/settings`).then((r) => r.json()).then(setSettings).catch(() => {});
    }
  }, [tier.name, tier.packageId, discountMode]);

  // Apply per-tier discount (same logic as PricingSection)
  const discountInfo = useMemo(() => {
    if (!discountMode || !settings || !serverPrice) return null;
    const enabled = settings[`${tier.packageId}_discount_enabled`];
    const type    = settings[`${tier.packageId}_discount_type`] || 'percent';
    const value   = Number(settings[`${tier.packageId}_discount_value`] || 0);
    // Flow tier discount applies anywhere; others require an active promo session/link.
    const applies = enabled && value > 0 && (tier.packageId === 'flow' ? true : shouldApplyDiscount);
    if (!applies) return null;
    const discounted = type === 'percent'
      ? Math.round(serverPrice * (1 - value / 100))
      : Math.max(0, serverPrice - value);
    return {
      original: serverPrice,
      discounted,
      label: type === 'percent' ? `-${value}%` : `-$${value}`,
    };
  }, [discountMode, settings, serverPrice, tier.packageId, shouldApplyDiscount]);

  const checkoutPrice = discountInfo ? discountInfo.discounted : serverPrice;

  // Format remaining time for the inline timer in hero
  const remainingFmt = useMemo(() => {
    const t = Math.max(0, remainingSeconds || 0);
    const h = String(Math.floor(t / 3600)).padStart(2, '0');
    const m = String(Math.floor((t % 3600) / 60)).padStart(2, '0');
    const s = String(t % 60).padStart(2, '0');
    return { h, m, s };
  }, [remainingSeconds]);

  const showTimer = discountMode && hasValidPromo && !isExpired && discountInfo;

  return (
    <div className="min-h-screen bg-[#08080A]">
      <Header />

      {/* Breadcrumb */}
      <div className="pt-20 sm:pt-24 px-4">
        <div className="max-w-5xl mx-auto pt-3">
          <Link to="/#pricing" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm" data-testid="back-to-tiers">
            <ChevronLeft className="w-4 h-4" /> Все тарифы
          </Link>
        </div>
      </div>

      {/* HERO — без цены, главное — кнопка */}
      <section className="relative px-4 py-10 sm:py-16">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-to-br from-current/10 to-transparent blur-[120px] rounded-full pointer-events-none opacity-30" style={{ color: 'currentColor' }} aria-hidden />

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex justify-center mb-5"
          >
            <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center shadow-2xl ${a.glow}`}>
              <div className={`absolute -inset-1 bg-gradient-to-br ${a.from} ${a.to} rounded-3xl blur-xl opacity-50`} aria-hidden />
              <HeroIcon className="relative w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
          </motion.div>

          {/* Tag */}
          <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3`} data-testid="hero-tagline">
            {tier.tagline}
          </p>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-5"
            data-testid="hero-title"
          >
            {tier.name}
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.5 }}
            className="text-zinc-300 text-base sm:text-lg lg:text-xl leading-relaxed max-w-3xl mx-auto mb-7"
          >
            {tier.hero_desc}
          </motion.p>

          {/* Chips */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.26 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-2.5 mb-9"
          >
            {tier.chips.map((c) => (
              <span key={c} className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold border ${a.chip}`}>
                {c}
              </span>
            ))}
          </motion.div>

          {/* Discount banner (only on /discount/* routes when a tier-discount is active) */}
          {discountInfo && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="max-w-2xl mx-auto mb-7"
              data-testid="tier-discount-banner"
            >
              <div className={`relative rounded-2xl overflow-hidden border-2 ${a.ring} bg-gradient-to-br from-amber-500/10 via-zinc-900 to-orange-500/10 shadow-2xl shadow-amber-500/20`}>
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/30 to-orange-500/30 rounded-3xl blur-2xl pointer-events-none animate-glow-soft" aria-hidden />
                <div className="relative p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Flame className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300 text-xs sm:text-sm font-black uppercase tracking-wider">Скидка {discountInfo.label}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-3 sm:gap-4 justify-center sm:justify-start flex-wrap">
                    <span className="text-zinc-500 line-through text-base sm:text-lg tabular-nums">${discountInfo.original}</span>
                    <ArrowRight className="w-4 h-4 text-amber-400" />
                    <span className="text-white text-2xl sm:text-3xl font-black tabular-nums">${discountInfo.discounted}</span>
                  </div>
                  {showTimer && (
                    <div className="flex items-center gap-1.5 tabular-nums text-amber-300 font-mono text-sm sm:text-base" data-testid="tier-discount-timer">
                      <Clock className="w-4 h-4" />
                      {remainingFmt.h}:{remainingFmt.m}:{remainingFmt.s}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* CTA — без цены */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col items-center gap-3"
          >
            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              className={`group relative inline-flex items-center justify-center gap-3 px-7 sm:px-10 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black text-white bg-gradient-to-r ${a.from} ${a.to} shadow-2xl ${a.glow} hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden w-full sm:w-auto max-w-md`}
              data-testid="hero-cta-buy"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative z-10">Оплатить — {tier.name}</span>
              <ArrowRight className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-zinc-500 text-xs sm:text-sm">Stripe • Visa/Mastercard • безопасный платёж</p>
          </motion.div>
        </div>
      </section>

      {/* INSTALLMENT — split into 2 monthly payments */}
      <section className="px-4 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-zinc-900 to-blue-500/10 p-5 sm:p-6 shadow-xl shadow-cyan-500/10" data-testid="tier-installment">
            <div className="absolute h-1 left-0 right-0 top-0 bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 rounded-t-2xl" aria-hidden />
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30 flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-cyan-300 text-[10px] sm:text-xs uppercase tracking-[0.18em] font-black mb-1">Удобная оплата</p>
                <h3 className="text-white font-black text-base sm:text-xl leading-tight">
                  Можно разбить на{' '}
                  <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">2 месяца</span>
                  {checkoutPrice > 0 && (
                    <> — по <span className="text-cyan-300 tabular-nums">${Math.ceil(checkoutPrice / 2).toLocaleString('en-US')}</span> в месяц</>
                  )}
                </h3>
                <p className="text-zinc-400 text-xs sm:text-sm mt-1.5 leading-relaxed">
                  Равными частями, без процентов и переплат. Для оплаты по-месячно — напишите{' '}
                  <a href="https://t.me/vadjik" target="_blank" rel="noopener noreferrer" className="text-cyan-300 font-bold hover:underline">@vadjik в Telegram</a>
                  , подберём график.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FITS / NOT FITS */}
      <section className="px-4 py-10 sm:py-14">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-4 sm:gap-5">
          <div className={`p-5 sm:p-6 rounded-2xl border ${a.ring} bg-zinc-900/60 backdrop-blur`}>
            <h3 className={`flex items-center gap-2 text-base sm:text-lg font-black mb-4 ${a.text}`}>
              <Check className="w-5 h-5" /> Подходит, если:
            </h3>
            <ul className="space-y-2.5">
              {tier.fits.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-zinc-300 text-sm sm:text-base leading-relaxed">
                  <Check className={`w-4 h-4 ${a.text} flex-shrink-0 mt-1`} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-5 sm:p-6 rounded-2xl border border-zinc-800 bg-zinc-900/60 backdrop-blur">
            <h3 className="flex items-center gap-2 text-base sm:text-lg font-black mb-4 text-zinc-400">
              <X className="w-5 h-5" /> Не подходит, если:
            </h3>
            <ul className="space-y-2.5">
              {tier.not_fits.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-zinc-500 text-sm sm:text-base leading-relaxed">
                  <X className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-1" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-4 py-10 sm:py-16 bg-zinc-900/30 border-y border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3`}>Что входит</p>
            <h2 className="text-3xl sm:text-5xl font-black text-white leading-tight">
              Полный список того,<br className="hidden sm:block" />
              <span className={`${a.text}`}> что вы получите</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {tier.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ delay: i * 0.06 }}
                  className={`p-5 sm:p-6 rounded-2xl bg-zinc-900/60 border ${a.ring}`}
                  data-testid={`feature-${i}`}
                >
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center mb-3 shadow-lg ${a.glow}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-white font-black text-base sm:text-lg mb-1.5 leading-tight">{f.title}</h3>
                  <p className="text-zinc-400 text-sm sm:text-base leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COURSE SCREENSHOT */}
      {tier.show_course_screenshot && (
        <section className="px-4 py-10 sm:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3`}>Платформа</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Так выглядит кабинет с уроками
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg mb-7 max-w-2xl mx-auto">
              SkillSpace — закрытая платформа: видео + домашние задания + ваш прогресс. Доступ открывается сразу после оплаты.
            </p>
            <div className={`relative rounded-2xl overflow-hidden border ${a.ring} shadow-2xl`}>
              <img src={COURSE_SCREENSHOT} alt="SkillSpace" className="w-full h-auto" loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* GROUP CALLS (Поток) */}
      {tier.show_group_calls && (
        <section className="px-4 py-10 sm:py-16 bg-zinc-900/30 border-y border-zinc-800/50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3`}>Групповые созвоны</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">9 живых сессий по 1.5 часа</h2>
              <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
                По одной в неделю. Каждый задаёт вопросы, каждый получает ответ. Все созвоны записываются — если пропустил, есть запись в чате.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
              {[FLOW_CALL_1, FLOW_CALL_2, FLOW_CALL_3].map((src, i) => (
                <div key={i} className={`relative rounded-xl overflow-hidden border ${a.ring}`}>
                  <img src={src} alt={`Созвон ${i+1}`} className="w-full h-auto" loading="lazy" />
                </div>
              ))}
            </div>
            <div className={`relative rounded-2xl overflow-hidden border ${a.ring}`}>
              <img src={GROUP_CHAT} alt="Чат группы" className="w-full h-auto" loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* ROADMAP */}
      {tier.show_roadmap && (
        <section className="px-4 py-10 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3`}>Маршрут</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white">
                {tier.show_roadmap === 'flow' ? 'Как пройдёт ваш месяц' : 'Как мы запустим ваш бизнес'}
              </h2>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {(tier.show_roadmap === 'flow' ? flowRoadmap : proRoadmap).map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-30px' }}
                    transition={{ delay: i * 0.05 }}
                    className={`flex gap-4 p-4 sm:p-5 rounded-xl bg-zinc-900/60 border ${a.ring}`}
                    data-testid={`roadmap-step-${i}`}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center shadow-lg ${a.glow}`}>
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className={`${a.text} text-[10px] sm:text-xs uppercase tracking-wider font-bold mb-0.5`}>{step.week}</p>
                      <h3 className="text-white font-black text-base sm:text-lg leading-tight mb-1">{step.title}</h3>
                      <p className="text-zinc-400 text-sm sm:text-base leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* VIP EXCLUSIVE (Pro only) */}
      {tier.show_vip_block && (
        <section className="px-4 py-10 sm:py-16 bg-zinc-900/30 border-y border-zinc-800/50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className={`${a.text} text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-3 flex items-center justify-center gap-2`}>
                <Crown className="w-4 h-4" /> VIP эксклюзив
              </p>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">$3 000+ в подарок только в Pro</h2>
              <p className="text-zinc-400 text-base sm:text-lg">Только для тарифа «Про уровень» — эти два продукта мы делаем под вас бесплатно.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
              <div className={`p-6 rounded-2xl border ${a.ring} bg-gradient-to-br from-emerald-500/10 via-zinc-900 to-teal-500/10 shadow-xl ${a.glow}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center mb-3 shadow-lg ${a.glow}`}>
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1">$1 500+ в подарок</p>
                <h3 className="text-white font-black text-xl mb-2">Кастомный веб-сайт</h3>
                <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">Персональный сайт с сегментацией аудитории, оплатой и премиум-дизайном. Под ваш бренд и ваших оптовиков.</p>
              </div>
              <div className={`p-6 rounded-2xl border ${a.ring} bg-gradient-to-br from-emerald-500/10 via-zinc-900 to-teal-500/10 shadow-xl ${a.glow}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center mb-3 shadow-lg ${a.glow}`}>
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1">$1 500+ в подарок</p>
                <h3 className="text-white font-black text-xl mb-2">CRM-система под вас</h3>
                <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">Кастомная CRM: контроль сотрудников, учёт брендов, задачи, чат и уведомления. Мощная база контроля воронки с каждым поставщиком.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="px-4 py-12 sm:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className={`mx-auto mb-5 w-14 h-14 rounded-2xl bg-gradient-to-br ${a.from} ${a.to} flex items-center justify-center shadow-2xl ${a.glow}`}>
            <HeroIcon className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-white leading-tight mb-4">
            {tier.final_message}
          </h2>
          <button
            type="button"
            onClick={() => setCheckoutOpen(true)}
            className={`group relative inline-flex items-center justify-center gap-3 px-7 sm:px-12 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black text-white bg-gradient-to-r ${a.from} ${a.to} shadow-2xl ${a.glow} hover:scale-[1.02] active:scale-[0.98] transition-all w-full sm:w-auto max-w-md mt-3`}
            data-testid="final-cta-buy"
          >
            <span>Оплатить — {tier.name}</span>
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-zinc-500 text-xs sm:text-sm mt-4">
            Если есть вопросы перед оплатой — пишите <a href="https://t.me/vadjik" target="_blank" rel="noopener noreferrer" className={`${a.text} font-bold hover:underline`}>@vadjik в Telegram</a>
          </p>
        </div>
      </section>

      {/* RESULTS */}
      <StudentResultsSection
        ctaLabel={`Хочу так же — взять ${tier.name}`}
        ctaOnClick={(e) => { e.preventDefault(); setCheckoutOpen(true); }}
      />

      <Footer />

      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        packageId={tier.packageId}
        packageName={tier.name}
        price={checkoutPrice}
        discountMode={discountMode}
      />
    </div>
  );
};

// Exported page-wrappers, one per route
export const MyselfPage   = () => <TierDetailPage tierKey="myself" />;
export const PotokPage    = () => <TierDetailPage tierKey="potok" />;
export const AdvancedPage = () => <TierDetailPage tierKey="advanced" />;
export const VipPage      = () => <TierDetailPage tierKey="vip" />;

// Discount variants — same content, but apply current per-tier discount + show timer if user has promo
export const DiscountMyselfPage   = () => <TierDetailPage tierKey="myself"   discountMode />;
export const DiscountPotokPage    = () => <TierDetailPage tierKey="potok"    discountMode />;
export const DiscountAdvancedPage = () => <TierDetailPage tierKey="advanced" discountMode />;
export const DiscountVipPage      = () => <TierDetailPage tierKey="vip"      discountMode />;
